import * as Sentry from "@sentry/vue";
import JSZip from "jszip";
import timezones from "@/helpers/timezones";
import { ArtifactType } from "@/enums/ArtifactType";
import DeviceUtility from "./device";

/**
 * Download file(s).
 * @param {Array} artifactUrls - An array of URLs to download.
 * @param {Array} artifactTitles - An array of name(s) to save the downloaded file(s) as.
 * @param {string} sessionOrProjectName - The name of the session or project.
 */
export async function downloadFiles(artifactUrls, artifactTitles, sessionOrProjectName = "") {
	if (artifactUrls.length == 0 || artifactTitles.length == 0) {
		return;
	}
	if (artifactUrls.length == 1) {
		return await downloadFile(artifactUrls[0], artifactTitles);
	}
	const zip = new JSZip();
	const datePart = timezones.formatDate(new Date().toISOString(), "YYYY-MM-DD_HHmmss");
	sessionOrProjectName = sessionOrProjectName.replace(/\s+/g, "-");
	sessionOrProjectName = sessionOrProjectName.replace(/[^a-zA-Z0-9-_]/g, "");
	const folderName = ("files" + "_" + sessionOrProjectName.substring(0, 25) + "_" + datePart).toLowerCase();
	const folder = zip.folder(folderName);

	const downloadFileIntoFolder = async (url, fileName) => {
		const separator = url.includes("?") ? "&" : "?";
		const fileUrl = url + separator + "timestamp=" + new Date().getTime();
		if (!fileName) {
			fileName = "file"; //fallback
		}

		try {
			const response = await fetch(fileUrl);
			if (!response.ok) {
				throw new Error(`Failed to fetch ${fileUrl}: ${response.statusText}`);
			}
			const blob = await response.blob();
			folder.file(fileName, blob);
		} catch (error) {
			console.error(`Failed to download file from ${fileUrl}:`, error);
			Sentry.captureException(error, {
				tags: {
					method: "downloadAsZip",
					file: "download.js",
				},
				extra: {
					fileUrl: fileUrl,
				},
			});
		}
	};

	const downloadPromises = artifactUrls.map((url, index) => {
		const fileName = artifactTitles[index];
		return downloadFileIntoFolder(url, fileName);
	});

	await Promise.all(downloadPromises);

	zip.generateAsync({ type: "blob" }).then((content) => {
		const link = document.createElement("a");
		link.href = URL.createObjectURL(content);
		link.download = `${folderName}.zip`;
		link.click();
	});
}

/**
 * Download a file from a given URL and save it with the specified file name.
 * @param {string} url - The URL of the file to download.
 * @param {string} fileName - The name to save the downloaded file as.
 */
export async function downloadFile(url, fileName) {
	try {
		const separator = url.includes("?") ? "&" : "?";
		url = url + separator + "timestamp=" + new Date().getTime();
		if (!fileName) {
			fileName = "file"; //fallback
		}
		const response = await fetch(url);
		if (!response.ok) {
			throw new Error(`Failed to fetch ${url}: ${response.statusText}`);
		}
		const blob = await response.blob();
		if (blob) {
			const blobUrl = URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.style.display = "none";
			a.href = blobUrl;
			a.download = fileName;
			document.body.appendChild(a);
			a.click();
			window.URL.revokeObjectURL(blobUrl);
		}
	} catch (err) {
		Sentry.captureException(err, {
			tags: {
				method: "downloadFile",
				file: "download.js",
			},
			extra: {
				fileUrl: url,
			},
		});
	}
}

/**
 * Generate a file name.
 * @param {string} artifactType - The type of artifact (e.g., "files", "snapshots", "recordings").
 * @param {Object} artifact - The artifact object containing the file information.
 * @param {string} parentId - The ID of the parent session or project.
 * @param {string} extension - The file extension (default is "mp4").
 */
export function generateFileName(artifactType, artifact, parentId, extension = "mp4") {
	if (artifactType === ArtifactType.Snapshots) {
		const fileName = artifact.src.split("?")[0];
		extension = fileName.split(".").pop();
	}
	return `file.${parentId}.${artifact.id}.${extension}`;
}

/**
 * Process and Download selected artifacts from a list of artifacts. Use it when you have to generate urls and titles first from a list of artifacts.
 * @param {string} artifactType - The type of artifact (e.g., "files", "snapshots", "recordings").
 * @param {string} parentName - The name of the parent session or project.
 * @param {string} parentId - The ID of the parent session or project.
 * @param {Array} artifacts - The list of artifacts.
 * @param {Array} selectedIds - The IDs of the selected artifacts.
 */
export async function processArtifactsAndDownload(artifactType, parentName, parentId, artifacts, selectedIds) {
	try {
		if (!artifacts || artifacts.length === 0 || selectedIds.length === 0) {
			return;
		}
		const artifactUrls = artifacts
			.filter((artifact) => selectedIds.includes(artifact.id))
			.map((artifact) => artifact.src);

		let artifactTitles = [];

		//files have titles by default that we need to preserve while downloading
		if (artifactType === ArtifactType.Files) {
			artifactTitles = artifacts
				.filter((artifact) => selectedIds.includes(artifact.id))
				.map((artifact) => artifact.title);
		} else {
			artifactTitles = artifacts
				.filter((artifact) => selectedIds.includes(artifact.id))
				.map((artifact) => generateFileName(artifactType, artifact, parentId));
		}
		await downloadFiles(artifactUrls, artifactTitles, parentName);
	} catch (err) {
		//caught in the downloadFiles function
	}
}

/***
 * Download an image from a canvas element.
 * @param {HTMLCanvasElement} canvas - The canvas element to download.
 * @param {string} fileName - The name of the file to save the image as (default is "canvas-image.png").
 */
export async function downloadFromCanvas (canvas, fileName = "canvas-image.png") {
	let link = document.createElement("a");
		link.download = fileName;
		link.target = "_blank";
		if (DeviceUtility.isFirefox) {
			const blob = await new Promise<Blob | null>((resolve) =>
				canvas.toBlob(resolve)
			);
			if (!blob) {
				throw new Error("Blob not created");
			}
			const url = URL.createObjectURL(blob);
			link.href = url;
		} else {
			link.href = canvas.toDataURL("image/png");
		}

		link.click();
}